/**
 * printPrefsStore.ts
 * Durable, cross-session print preferences so the user does not have to
 * re-pick them every time they open the app:
 *   - grouping column (Nhóm Dữ Liệu)
 *   - paper size + orientation + custom mm (Khổ Giấy)
 *   - last applied printer profile id (Máy In)
 *
 * Unlike the per-file session store, these persist across different Excel /
 * template files. Purely client-side (no PII).
 */

import type { CustomPaperMm, Orientation, PaperSize } from "@/types/print";

const STORAGE_KEY = "tvs-excel-uploader:print-prefs";

export interface PrintPrefs {
  /** Primary grouping column (Nhóm Dữ Liệu). */
  groupingColumn?: string;
  /** Paper size override (empty = follow template). */
  paperSize?: PaperSize;
  /** Orientation override (empty = auto). */
  orientation?: Orientation;
  /** Custom dimensions when paperSize === "Custom". */
  customMm?: CustomPaperMm;
  /** Id of the last applied printer profile. */
  printerProfileId?: string;
}

const DEFAULTS: PrintPrefs = {};

export function loadPrintPrefs(): PrintPrefs {
  if (typeof localStorage === "undefined") return { ...DEFAULTS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<PrintPrefs>;
    return {
      ...DEFAULTS,
      ...(typeof parsed.groupingColumn === "string" ? { groupingColumn: parsed.groupingColumn } : {}),
      ...(typeof parsed.paperSize === "string" ? { paperSize: parsed.paperSize as PaperSize } : {}),
      ...(typeof parsed.orientation === "string" ? { orientation: parsed.orientation as Orientation } : {}),
      ...(parsed.customMm && typeof parsed.customMm.width === "number" && typeof parsed.customMm.height === "number"
        ? { customMm: { width: parsed.customMm.width, height: parsed.customMm.height } }
        : {}),
      ...(typeof parsed.printerProfileId === "string" ? { printerProfileId: parsed.printerProfileId } : {}),
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function savePrintPrefs(prefs: PrintPrefs): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}
